// --- START OF FILE src/creator.js ---
import React, { useState, useEffect, useRef } from 'react';
import Header from './components/Header';
import PreviewSection from './components/PreviewSection';
import EditorSection from './components/EditorSection';
import DesktopClassicLayout from './components/DesktopClassicLayout'; // Implemented classic layout
import ImportModal from './components/modals/ImportModal';
import DebugModal from './components/modals/DebugModal';
import TribeModal from './components/modals/TribeModal';
import CustomAssetsModal from './components/modals/CustomAssetsModal';
import ArtModal from './components/modals/ArtModal';
import CardCreatedModal from './components/modals/CardCreatedModal';
import { initialCardParams } from './constants';
import './index.css';
import { FaCheck } from "react-icons/fa";

// --- Helper: Retrieve perfect retrofitting offsets for flattened card imports ---
const getHearthcardsOffsets = (type, rarity) => {
  const t = type ? type.toLowerCase() : 'minion';
  const r = rarity ? rarity.toLowerCase() : 'common';
  let x = 0, y = 30, w = 785, h = 1100;

  if (t === 'minion') {
    x = 0; y = 30; w = 785; h = 1100;
  } else if (t === 'spell') {
    if (r === 'legendary') { x = 0; y = 30; w = 825; h = 1100; }
    else { x = 0; y = 30; w = 785; h = 1100; }
  } else if (t === 'weapon') {
    x = 0; y = 60; w = 785; h = 1070;
  } else if (t === 'location') {
    if (r === 'legendary') { x = 0; y = 0; w = 795; h = 1130; }
    else { x = 0; y = 0; w = 795; h = 1120; }
  } else if (t === 'herocard') {
    x = 0; y = 25; w = 785; h = 1105;
  } else {
    x = -1; y = 28; w = 787; h = 1103;
  }

  return { x, y, w, h };
};

export default function App() {
  const [cardParams, setCardParams] = useState(initialCardParams);
  const [activeModal, setActiveModal] = useState(null); 
  const [actualFontSize, setActualFontSize] = useState(0);
  const [toast, setToast] = useState(null);
  const toastTimeoutRef = useRef(null);

  const canvasRef = useRef(null);
  const [createdCardData, setCreatedCardData] = useState(null);

  const [editConfig, setEditConfig] = useState({
    editMode: '',
    nid: '',
    redirect: ''
  });

  // Track Display mode for Legacy Desktop Layout
  const [isDesktop, setIsDesktop] = useState(window.innerWidth > 950);

  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth > 950);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const overwriteAllParams = (newParams) => setCardParams(newParams);

  const handleCreateSubmit = async () => {
    if (!canvasRef.current) return;
    showToast("Generating high-res card artwork...");
    try {
      const base64Img = await canvasRef.current.getCardImageBase64();
      if (base64Img) {
        setCreatedCardData(base64Img); // Sets the image data which opens the modal
      } else {
        showToast("Error generating card image.");
      }
    } catch (e) {
      showToast("Generation failed.");
    }
  };

  // --- Global DB Card Importer ---
  const importCardById = async (cardId) => {
    try {
      const formData = new FormData();
      formData.append('cardname', cardId);

      const res = await fetch('/ajax/importer_main.php', { method: 'POST', body: formData });
      const data = await res.json();

      // Resolve Art source pathway
      let artFile = "";
      if (data.hearthcards === '1') {
        artFile = data.ext === 'png' ? `temp/${data.art}.png` : `cards/${data.art}.webp`;
      } else {
        artFile = `art/${data.art}.jpg`;
      }

      let importedParams = {};
      if (data.json) {
        try {
          importedParams = JSON.parse(data.json);
        } catch (e) {
          console.warn("Failed to parse nested card JSON, falling back to database fields.");
        }
      }

      // Calculate legacy alignment if coordinates aren't defined inside JSON payload
      let finalX = null, finalY = null, finalScale = null;

      if (data.hearthcards === '1') {
        const offsets = getHearthcardsOffsets(data.type, data.rarity);
        console.log("Calculated Hearthcards offsets:", offsets);
        try {
          const img = new Image();
          img.src = artFile.startsWith('/') ? artFile : `/${artFile}`;
          await new Promise((resolve, reject) => {
            img.onload = resolve;
            img.onerror = reject;
          });
          finalX = offsets.x;
          finalY = offsets.y;
          finalScale = offsets.w / img.naturalWidth;
        } catch (e) {
          console.warn("Could not load flattened card to auto-calculate offsets", e);
        }
      }

      if (Object.keys(importedParams).length > 0) {
        setCardParams({
          ...initialCardParams,
          ...importedParams,
          Art: {
            File: artFile,
            originalUrl: importedParams.Art?.originalUrl || "",
            x: finalX,
            y: finalY,
            scale: finalScale,
            disableDropShadow: data.hearthcards === '1' 
          }
        });
      } else {
        let dualClass = "";
        let mainClass = data.class ? (data.class.charAt(0).toUpperCase() + data.class.slice(1)) : "Neutral";
        let banner = "";

        if (data.dualclass === 'kabal') { mainClass = "Neutral"; banner = "banner_triclass_0-1.webp"; }
        else if (data.dualclass === 'grimy_goons') { mainClass = "Neutral"; banner = "banner_triclass_0-2.webp"; }
        else if (data.dualclass === 'jade_lotus') { mainClass = "Neutral"; banner = "banner_triclass_0-3.webp"; }
        else if (data.dualclass) {
          const split = data.dualclass.split('_');
          mainClass = split[0].charAt(0).toUpperCase() + split[0].slice(1);
          dualClass = split[1].charAt(0).toUpperCase() + split[1].slice(1);
        }

        let runes = { Slot1: "", Slot2: "", Slot3: "" };
        let runeIdx = 1;
        for (let i = 0; i < (data.cost_blood || 0); i++) runes[`Slot${runeIdx++}`] = 'CardRunes_DeathKnight_Blood.webp';
        for (let i = 0; i < (data.cost_frost || 0); i++) runes[`Slot${runeIdx++}`] = 'CardRunes_DeathKnight_Frost.webp';
        for (let i = 0; i < (data.cost_unholy || 0); i++) runes[`Slot${runeIdx++}`] = 'CardRunes_DeathKnight_Unholy.webp';

        setCardParams({
          ...initialCardParams,
          Type: data.type || "minion",
          Class: mainClass,
          DualClass: dualClass,
          Banner: data.tradeable === '1' ? "banner_tradeable.webp" : banner,
          Rarity: data.rarity ? (data.rarity.charAt(0).toUpperCase() + data.rarity.slice(1)) : "Common",
          Title: data.name || "",
          Tribe: data.race || "",
          Cost: { Type: data.bgstars > 0 ? "BGStars" : "Mana", Value: data.bgstars > 0 ? data.bgstars : (data.cost || "") },
          Attack: data.attack || "",
          Health: data.health || "",
          Text: data.desc || "",
          Emblem: data.setname || "CATAIcon",
          Runes: runes,
          Art: { 
            File: artFile, 
            originalUrl: "", 
            x: finalX, 
            y: finalY, 
            scale: finalScale,
            disableDropShadow: data.hearthcards === '1' 
          }
        });
      }
      showToast(`Successfully imported card`); // #${cardId}
    } catch (err) {
      console.error("Preset URL import failed", err);
    }
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlAction = urlParams.get('action');
    const urlId = urlParams.get('id');
    const urlNId = urlParams.get('nid');
    const urlRedirect = urlParams.get('redirect');

    if (urlAction === 'edit' && urlId) {
      // 1. If in Edit Mode, store config and import card
      setEditConfig({
        editMode: urlId,
        nid: urlNId || '',
        redirect: urlRedirect || ''
      });
      importCardById(urlId.trim());
    } else {
      // 2. Fallback to standard hash-based or search-query preset auto-importer
      let presetId = window.location.hash ? window.location.hash.replace('#', '') : null;
      if (!presetId) {
        presetId = urlParams.get('id') || urlParams.get('import');
      }

      if (presetId && presetId.trim() !== '') {
        importCardById(presetId.trim());
      }
    }
  }, []);

  // --- Parse Preset Params on Page Load ---
  useEffect(() => {
    // 1. Read Hash (e.g., domain.com/#69925)
    let presetId = window.location.hash ? window.location.hash.replace('#', '') : null;

    // 2. Fallback to Search Query Parameters (e.g., domain.com/?id=69925)
    if (!presetId) {
      const urlParams = new URLSearchParams(window.location.search);
      presetId = urlParams.get('id') || urlParams.get('import');
    }

    // Process if a valid ID was found
    if (presetId && presetId.trim() !== '') {
      importCardById(presetId.trim());
    }
  }, []);

  // --- Scroll Lock for Modals ---
  useEffect(() => {
    if (activeModal) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [activeModal]);

  const updateParam = (key, value) => {
    setCardParams(prev => {
      if (key === 'Type' && prev.Type !== value) {
        return { 
          ...prev, 
          [key]: value, 
          Art: { 
            ...prev.Art, 
            x: null, 
            y: null, 
            scale: null 
          } 
        };
      }
      if (key === 'Rarity' && prev.Rarity !== value) {
        return { 
          ...prev, 
          [key]: value, 
          Overwrite: {
            ...prev.Overwrite,
            Dragon: "",
            Emblem: ""
          } 
        };
      }
      return { ...prev, [key]: value };
    });
  };

  const updateNestedParam = (parent, key, value) => {
    setCardParams(prev => ({ ...prev, [parent]: { ...prev[parent], [key]: value } }));
  };

 const exportJSON = async () => {
    showToast("Preparing JSON export...");
    
    // Deep clone the card parameters to keep state clean during export processing
    const exportParams = JSON.parse(JSON.stringify(cardParams));

    if (exportParams.Art) {
      const fileVal = exportParams.Art.File || "";
      const originalUrlVal = exportParams.Art.originalUrl || "";

      // Case 1: A remote URL was used.
      // We restore the remote URL back to Art.File and do NOT encode to Base64.
      if (originalUrlVal.trim().startsWith('http')) {
        exportParams.Art.File = originalUrlVal.trim();
      } 
      // Case 2: A local file was uploaded.
      // File starts with 'blob:' and there is no remote originalUrl fallback. We embed it as Base64.
      else if (fileVal.startsWith('blob:')) {
        try {
          const response = await fetch(fileVal);
          const blob = await response.blob();
          const base64Data = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });

          if (base64Data) {
            exportParams.Art.File = base64Data;
          }
        } catch (err) {
          console.warn("Failed to embed local image blob into JSON file, exporting reference as-is.", err);
        }
      }
    }

    const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(exportParams, null, 2));
    const a = document.createElement('a');
    a.href = dataStr;
    a.download = `${exportParams.Title.replace(/\s+/g, '-') || 'card'}_export.json`;
    a.click();
    showToast("JSON exported successfully");
  };

  const importJSON = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const parsed = JSON.parse(event.target.result);

        // Sanity-check and repair expired session blob URLs back to the remote URL if possible
        if (parsed.Art) {
          const currentFile = parsed.Art.File || "";
          const originalUrl = parsed.Art.originalUrl || "";

          if ((currentFile.startsWith('blob:') || !currentFile) && originalUrl.trim().startsWith('http')) {
            parsed.Art.File = originalUrl.trim();
          }
        }

        setCardParams({ ...initialCardParams, ...parsed });
        showToast("JSON card imported successfully");
      } catch (err) {
        alert("Invalid JSON file.");
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const showToast = (message) => {
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    setToast(message);
    toastTimeoutRef.current = setTimeout(() => setToast(null), 1500);
  };

  const dismissToast = () => {
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    setToast(null);
  };

  const handleCustomAssetSelect = (modalType, assetId) => {
    if (modalType === 'customClass') updateParam('Class', assetId);
    if (modalType === 'customDualClass') updateParam('DualClass', assetId);
    if (modalType === 'customEmblem') updateParam('Emblem', assetId);
    if (modalType === 'customBanner') updateParam('Banner', `banners/${assetId}.png`);
    if (modalType === 'customCardType') updateParam('Type', assetId);
    setActiveModal(null);
  };

  return (
    <>
      <div className="font_preload">
        <span style={{fontFamily: "'Belwe', Arial, sans-serif"}}></span>
        <span style={{fontFamily: "'FranklinGothic', Arial, sans-serif"}}></span>
        <span style={{fontFamily: "'BenguiatBoldITC', Arial, sans-serif"}}></span>
      </div>

      {isDesktop ? (
         <DesktopClassicLayout 
           cardParams={cardParams} 
           updateParam={updateParam}
           updateNestedParam={updateNestedParam}
           overwriteAllParams={overwriteAllParams}
           onOpenModal={setActiveModal}
           onReset={() => setCardParams(initialCardParams)}
           actualFontSize={actualFontSize}
           onCreate={handleCreateSubmit}
           canvasRef={canvasRef}
           onExport={exportJSON}
           onImport={importJSON}
           showToast={showToast}
         />
      ) : (
        <div className="creator-container pt-3" data-bs-theme="dark">
          <main className="container-fluid p-0 pt-2 p-lg-4 flex-grow-1" style={{ maxWidth: '1400px' }}>
            <div className="row g-0 gx-lg-5">
              <div className="col-lg-5 col-xl-5 mb-4 mb-lg-0">
                <PreviewSection 
                  cardParams={cardParams} 
                  updateParam={updateParam} 
                  onExport={exportJSON}
                  onImport={importJSON}
                  onOpenModal={setActiveModal}
                  onTextRendered={(size) => setActualFontSize(size)}
                  showToast={showToast}
                  cardCanvasRef={canvasRef}
                />
              </div>

              <div className="col-lg-7 col-xl-7">
                <EditorSection 
                  cardParams={cardParams} 
                  updateParam={updateParam} 
                  updateNestedParam={updateNestedParam}
                  overwriteAllParams={overwriteAllParams}
                  onOpenModal={setActiveModal}
                  onReset={() => setCardParams(initialCardParams)}
                  actualFontSize={actualFontSize}
                  onCreate={handleCreateSubmit}
                />
              </div>
            </div>
          </main>
        </div>
      )}

      {toast && (
        <div className="hc-toast-container">
          <div className="hc-toast" onClick={dismissToast}>
            <FaCheck className="text-success me-3" style={{ width: '24px', height: '24px' }}/>
            <span>{toast}</span>
          </div>
        </div>
      )}

      <ImportModal 
        isOpen={activeModal === 'import'} 
        onClose={() => setActiveModal(null)} 
        onImportCard={importCardById} 
        cardParams={cardParams}
        setCardParams={setCardParams} 
      />

      <CardCreatedModal
        isOpen={!!createdCardData}
        onClose={() => setCreatedCardData(null)}
        base64={createdCardData}
        cardParams={cardParams}
        editConfig={editConfig}
      />

      <ArtModal isOpen={activeModal === 'art'} onClose={() => setActiveModal(null)} cardParams={cardParams} setCardParams={setCardParams} />
      <DebugModal isOpen={activeModal === 'debug'} onClose={() => setActiveModal(null)} cardParams={cardParams} setCardParams={setCardParams} />
      <TribeModal isOpen={activeModal === 'tribe'} onClose={() => setActiveModal(null)} cardParams={cardParams} setCardParams={setCardParams} />
      <CustomAssetsModal isOpen={['customClass', 'customDualClass', 'customEmblem', 'customBanner', 'customCardType'].includes(activeModal)} onClose={() => setActiveModal(null)} modalType={activeModal} onSelect={(id) => handleCustomAssetSelect(activeModal, id)} />
    </>
  );
}